/**
 * Error Boundary Component
 *
 * Catches React errors and displays fallback UI.
 * Prevents entire app from crashing when component errors occur.
 */

import { Component, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { ErrorPage } from './ErrorPage/ErrorPage';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary
 *
 * Root-level error boundary to catch unhandled React errors
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logger.error('ErrorBoundary caught error', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          title="Something went wrong"
          message="An unexpected error occurred. Please reload the page to try again."
        />
      );
    }

    return this.props.children;
  }
}
